import { cityByAirport, isValidCpf, maskCpf } from '@voll-intelligence/domain';
import {
  allocationOwnerId,
  hotelRooms,
  computeSubmitBlockers,
  conciergeFieldsFor,
  offerFieldsFor,
} from '@voll-intelligence/genui-contract';
import type {
  CheckoutEnv,
  CheckoutIo,
  CheckoutRules,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useMemo } from 'react';
import { useGenUiConfig, type GenUiConfig } from '../../../shared/ui/config.js';
import { genUiI18n } from '../../../shared/ui/i18n.js';
import { checkoutCopy } from '../ui/checkout/checkout_copy.js';

/** The wire contract's checkout rules in the shape the kit's organism takes:
 *  the screen, the submit builder and the BFF judge by the same functions. */
export const checkoutRules: CheckoutRules = {
  offerFields: offerFieldsFor,
  submitBlockers: computeSubmitBlockers,
  allocationOwnerId,
  hotelRooms,
  conciergeFields: conciergeFieldsFor,
  isValidCpf,
  maskCpf,
};

type PhotosFor = NonNullable<CheckoutIo['getTravelerPhotos']>;

const composedIo = new WeakMap<CheckoutIo, { photosFor: PhotosFor; io: CheckoutIo }>();

/**
 * The checkout port plus the traveler photo read (its own port, shared with the
 * voucher). The composed IO keeps its identity while both reads do: the forms
 * treat a new IO as a new host and reload, which would drop an open drawer's
 * drafts whenever an unrelated part of the config changes.
 */
function checkoutIoOf(port: CheckoutIo | undefined, photosFor: PhotosFor | undefined) {
  if (!port) return null;
  if (!photosFor) return port;
  const hit = composedIo.get(port);
  if (hit?.photosFor === photosFor) return hit.io;
  const io: CheckoutIo = { ...port, getTravelerPhotos: photosFor };
  composedIo.set(port, { photosFor, io });
  return io;
}

/**
 * The kit checkout's environment built from a `GenUiConfig` (#5000): the
 * `productCheckout` port, the contract rules, the catalog texts in the config's
 * language, the domain's airport cities and the telemetry port. Pure: the chat
 * shell calls it with the context's config, the web-chat with its own.
 */
export function checkoutEnvOf(config: GenUiConfig): CheckoutEnv {
  const { locale } = genUiI18n(config.locale);
  const telemetry = config.telemetry;
  return {
    io: checkoutIoOf(config.ports?.productCheckout, config.ports?.travelerPhotos?.photosFor),
    rules: checkoutRules,
    copy: checkoutCopy(locale),
    locale,
    cityOf: (iata) => cityByAirport(iata) ?? undefined,
    track: (event, props) => {
      telemetry?.track(event, props);
    },
  };
}

/** The checkout environment from the context's `GenUiConfig`. */
export function useCheckoutEnv(): CheckoutEnv {
  const config = useGenUiConfig();
  return useMemo(() => checkoutEnvOf(config), [config]);
}
